import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CampaignChannel } from "../../Engagements/EngagementInterfaces";
import { setCampaignStatus } from "src/services/ChatbotAPI";
import { useCampaignDraft } from "src/layouts/Engagements/Campaigns/shared/UseCampaignDraft";
import { useChatCampaignSetup, type ChatCampaignSetupResult } from "./UseChatCampaignSetup";
import { useChatEmailCampaignWizard, type ChatWizardStepSnapshot } from "./UseChatEmailCampaignWizard";
import { useChatWhatsAppCampaignWizard, type ChatWhatsAppStepSnapshot } from "./useChatWhatsAppCampaignWizard";
import { useChatTemplateCampaignWizard, type ChatTemplateStepSnapshot } from "./UseChatTemplateCampaignWizard";

export type ChatCampaignFlowPhase = "channel" | "setup" | "email" | "template" | "whatsappMode" | "whatsapp";

export type ChatCampaignFlowSnapshot =
  | { phase: "channel"; channel: CampaignChannel }
  | { phase: "setup"; result?: ChatCampaignSetupResult }
  | { phase: "email"; snapshot: ChatWizardStepSnapshot }
  | { phase: "template"; snapshot: ChatTemplateStepSnapshot }
  | { phase: "whatsappMode"; useAi: boolean }
  | { phase: "whatsapp"; snapshot: ChatWhatsAppStepSnapshot };

interface UseChatCampaignFlowParams {
  onAdvance: (_snapshot: ChatCampaignFlowSnapshot) => void;
  presetChannel?: CampaignChannel;
}

const getChannelStartPhase = (channel: CampaignChannel): ChatCampaignFlowPhase =>
  channel === "whatsapp" ? "whatsappMode" : "setup";

export const useChatCampaignFlow = ({ onAdvance, presetChannel }: UseChatCampaignFlowParams) => {
  const [currentPhase, setPhase] = useState<ChatCampaignFlowPhase>("channel");
  const phase =
    currentPhase === "channel" && presetChannel ? getChannelStartPhase(presetChannel) : currentPhase;
  const [campaignData, setCampaignData] = useState<Partial<ChatCampaignSetupResult>>({});
  const [whatsAppUseAi, setWhatsAppUseAi] = useState(false);
  const draftController = useCampaignDraft();

  const setup = useChatCampaignSetup({
    draftController,
    presetChannel,
    onSubmit: result => {
      onAdvance({ phase: "setup", result });
      setCampaignData(result);
      setPhase(result.isAIGeneratorOn ? "email" : "template");
    },
  });

  const emailWizard = useChatEmailCampaignWizard({
    campaignData,
    onAdvance: snapshot => onAdvance({ phase: "email", snapshot }),
    draftController,
  });

  const backToSetup = () => setPhase("setup");

  const templateWizard = useChatTemplateCampaignWizard({
    campaignData,
    onAdvance: snapshot => onAdvance({ phase: "template", snapshot }),
    onBackToSetup: backToSetup,
    draftController,
  });

  const whatsAppWizard = useChatWhatsAppCampaignWizard({
    useAi: whatsAppUseAi,
    onAdvance: snapshot => onAdvance({ phase: "whatsapp", snapshot }),
    onBackToMode: () => setPhase("whatsappMode"),
  });

  const { setChannel } = setup;
  const chooseChannel = useCallback(
    (channel: CampaignChannel) => {
      onAdvance({ phase: "channel", channel });
      setChannel(channel);
      setPhase(getChannelStartPhase(channel));
    },
    [onAdvance, setChannel]
  );

  const chooseWhatsAppMode = useCallback(
    (useAi: boolean) => {
      onAdvance({ phase: "whatsappMode", useAi });
      setWhatsAppUseAi(useAi);
      setPhase("whatsapp");
    },
    [onAdvance]
  );

  // Re-seed the destination wizard the moment setup hands off fresh
  // campaignData, so campaignName/sendTo/etc. aren't stuck at the stale
  // empty values captured when these hooks first mounted.
  const campaignDataRef = useRef(campaignData);
  useEffect(() => {
    if (campaignDataRef.current === campaignData) {
      return;
    }
    campaignDataRef.current = campaignData;
    if (!campaignData.campaignName) {
      return;
    }
    if (phase === "email") {
      emailWizard.reset();
    } else if (phase === "template") {
      templateWizard.reset();
    }
  }, [campaignData, emailWizard, phase, templateWizard]);

  const { closeDraft } = draftController;
  const resetFlow = useCallback(() => {
    closeDraft();
    setCampaignStatus("none");
    setPhase("channel");
    setCampaignData({});
    setWhatsAppUseAi(false);
    setup.reset();
    emailWizard.reset();
    templateWizard.reset();
    whatsAppWizard.wizard.reset();
  }, [closeDraft, emailWizard, setup, templateWizard, whatsAppWizard]);

  return useMemo(
    () => ({
      phase,
      setup,
      campaignData,
      emailWizard,
      templateWizard,
      whatsAppWizard,
      backToSetup,
      chooseChannel,
      chooseWhatsAppMode,
      resetFlow,
    }),
    [
      phase,
      setup,
      campaignData,
      emailWizard,
      templateWizard,
      whatsAppWizard,
      chooseChannel,
      chooseWhatsAppMode,
      resetFlow,
    ]
  );
};
